import React, { useId } from "react";
import type { ElementType } from "react";
import clsx from "clsx";

/**
 * Tipos auxiliares para suporte polimórfico com ref seguro.
 */
type AsProp<C extends ElementType> = {
  as?: C;
};

type PropsToOmit<C extends ElementType, P> = keyof (AsProp<C> & P);

export type PolymorphicRef<C extends ElementType> = React.ComponentPropsWithRef<C>["ref"];

export type PolymorphicComponentProps<
  C extends ElementType,
  Props = Record<string, never>
> = React.PropsWithChildren<Props & AsProp<C>> &
  Omit<React.ComponentPropsWithoutRef<C>, PropsToOmit<C, Props>>;

/**
 * Props do componente Card.
 */
export interface CardOwnProps {
  children: React.ReactNode;
  theme?: "light" | "dark";
  header?: React.ReactNode;
  footer?: React.ReactNode;
  outlined?: boolean;
  shadow?: boolean | "sm" | "md" | "lg";
  className?: string;
  tabIndex?: number;
  "aria-label"?: string;
  "aria-describedby"?: string;
  role?: string;
}

/**
 * Componente Card polimórfico, com tipagem de ref segura.
 */
type CardProps<C extends ElementType = "div"> = PolymorphicComponentProps<C, CardOwnProps>;

// CardInner must be a non-generic function for React.forwardRef
function CardInnerBase(
  {
    children,
    theme = "light",
    as,
    header,
    footer,
    outlined = false,
    shadow = "md",
    className,
    tabIndex,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedBy,
    role,
    ...rest
  }: CardProps<"div">,
  ref?: PolymorphicRef<"div">
) {
  const Component = as || "div";
  const headerId = useId();

  // Semântica
  let semanticRole = role;
  if (!role && typeof Component === "string") {
    const tag = Component as string;
    if (tag === "section" || tag === "article") {
      semanticRole = "region";
    }
  }

  // Tema

  // Sombra
  const shadowClass =
    shadow === false ? "shadow-none" :
    shadow === "sm"   ? "shadow-sm"   :
    shadow === "lg"   ? "shadow-lg"   :
    /* md, true ou undefined */         "shadow-md";

  // Patch: Garante que o tema nunca venha errado do backend/dos props
  // Se theme não for "dark", força "light"
  const safeTheme = theme === "dark" ? "dark" : "light";
  const safeThemeClass =
    safeTheme === "dark"
      ? "bg-zinc-900 text-zinc-100"
      : "bg-white text-zinc-900";

  return (
    <Component
      className={clsx(
        "rounded-lg p-4 my-2 transition-colors transition-shadow duration-300 relative overflow-hidden w-full max-w-full",
        safeThemeClass,
        outlined
          ? safeTheme === "dark"
            ? "border border-neutral-700"
            : "border border-neutral-300"
          : "",
        shadowClass,
        className
      )}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-labelledby={header ? headerId : undefined}
      role={semanticRole}
      ref={ref}
      data-testid="card" // <-- Adicionado para facilitar os testes
      {...rest}
    >
      {header && (
        <div
          className={clsx(
            "font-bold mb-2 px-1 text-base md:text-lg flex items-center",
            safeTheme === "dark" ? "text-white" : "text-zinc-900"
          )}
          id={headerId}
        >
          {header}
        </div>
      )}
      <div className="w-full h-full">{children}</div>
      {footer && (
        <div
          className={clsx(
            "mt-2 text-sm px-1",
            safeTheme === "dark" ? "text-zinc-400" : "text-zinc-500"
          )}
        >
          {footer}
        </div>
      )}
    </Component>
  );
}

// Cast para polimorfismo
type CardComponent = <C extends ElementType = "div">(
  props: CardProps<C> & { ref?: PolymorphicRef<C> }
) => React.ReactElement | null;

const Card = React.forwardRef(CardInnerBase) as CardComponent & React.ForwardRefExoticComponent<CardProps<ElementType> & React.RefAttributes<ElementType>>;

Card.displayName = "Card";

/**
 * Card polimórfico estilizado com Tailwind CSS.
 *
 * Componente de cartão flexível, com suporte a tema claro/escuro, sombra, borda, cabeçalho, rodapé,
 * acessibilidade e polimorfismo de elemento HTML.
 *
 * - Usa apenas Tailwind para estilização, sem dependência de CSS externo.
 * - Responsivo por padrão (`w-full max-w-full`).
 * - Suporte a temas `light` e `dark`.
 * - Suporte a diferentes níveis de sombra (`shadow`, `shadow-sm`, `shadow-md`, `shadow-lg`, `shadow-none`).
 * - Suporte a borda (`outlined`).
 * - Cabeçalho e rodapé opcionais.
 * - Polimórfico: pode renderizar qualquer elemento HTML via prop `as`.
 * - Acessível: suporta `aria-label`, `aria-describedby`, `aria-labelledby`, `role`, e foco via `tabIndex`.
 *
 * @template C ElementType do container (default: "div")
 * @param {CardProps<C>} props
 * @param {PolymorphicRef<C>} ref
 *
 * @example
 * // Card simples
 * <Card>
 *   Conteúdo do cartão
 * </Card>
 *
 * @example
 * // Card escuro, com sombra grande e cabeçalho
 * <Card theme="dark" shadow="lg" header="Título">
 *   <p>Conteúdo</p>
 * </Card>
 *
 * @example
 * // Card com borda, rodapé e papelaria personalizada
 * <Card outlined footer={<span>Rodapé</span>} className="max-w-md mx-auto">
 *   <p>Conteúdo centralizado</p>
 * </Card>
 *
 * @example
 * // Card polimórfico usando <section>
 * <Card as="section" header="Seção">
 *   <p>Conteúdo da seção</p>
 * </Card>
 *
 * @example
 * // Card acessível com aria-label e tabIndex
 * <Card aria-label="Informação importante" tabIndex={0}>
 *   <strong>Conteúdo acessível</strong>
 * </Card>
 */
export default Card;